<template>
    <div class="stala-designer" >
        <div class="loading" v-if="loading"></div>

        <div class="designer-unavailable-view view" v-if="viewportTooNarrow && ready" :style="unavailableViewStyles()">
            <div class="wrapper">
                <div class="html-text" v-html="ui.texts.designerUnavailableText.text"></div>
            </div>
        </div>

        <template v-if="ready && !viewportTooNarrow">
            <div class="header">
                <div class="wrapper">
                    <mini-cart />

                    <div class="actions" v-if="!state.externalEmbed">
                        <a href="#" class="cart" @click.prevent.stop="toggleCart">
                            <span class="icon-th-list"></span>
                            <span class="count">{{ cart.productCount }}</span>
                        </a>

                        <a href="#" class="languages" @click.prevent.stop="ui.languagesVisible = !ui.languagesVisible" v-if="ui.languages.length > 0 && !state.proshop">
                            <span class="icon-globe"></span>
                            <span class="title">{{ ui.language }}</span>

                            <div class="language-list" v-show="ui.languagesVisible">
                                <a href="#" @click.prevent.stop="setLanguage(language)" v-for="language in ui.languages" :key="language">
                                    {{ language  }}
                                </a>
                            </div>
                        </a>

                        <a href="javascript:void(Tawk_API.toggle())" class="chat" v-if="ui.chatEnabled">
                            <span class="icon-comments"></span>
                        </a>
                    </div>

                    <a :href="configuration.homePageLink ?? '/'" class="logo" v-if="!state.proshop && !state.externalEmbed">
                        <img src="../../less/img/logo-alt.png" />
                    </a>

                    <router-link to="/" class="home" v-if="!state.externalEmbed">
                        <span class="icon-home"></span>
                    </router-link>

                    <div class="menu" v-if="state.mode == 'worktop'">
                        <router-link to="/family" v-if="state.allowNewDesigner" :class="{ complete: state.productFamily }">
                            {{ ui.t('productFamilyTabTitle') }}
                        </router-link>

                        <span class="relative inline-block">
                            <router-link :to="`/${state.productFamilyCode}/worktop`" :class="{ complete: ui.worktopSelectionsComplete }">
                                {{ ui.t('worktopTabTitle') }}
                            </router-link>
                            <span class="input-tip bottom info" v-if="ui.showNewWorktopOptionsAvailableTip">
                                {{ ui.t('crosslinkNewChoicesAvailable') }}
                            </span>
                        </span>

                        <router-link :to="`/${state.productFamilyCode}/material`" :class="{ complete: ui.materialSelectionsComplete, accessible: ui.worktopSelectionsComplete }">
                            {{ ui.t('worktopMaterialTabTitle') }}
                        </router-link>

                        <router-link :to="`/${state.productFamilyCode}/sink`" :class="{ complete: ui.sinkSelectionsComplete, invalid: !ui.sinkIsValid || ui.sinkHasPlacementError, accessible: ui.materialSelectionsComplete }">
                            {{ ui.t('sinkTabTitle') }}
                        </router-link>

                        <router-link :to="`/${state.productFamilyCode}/hobcutout`" :class="{ complete: ui.hobcutoutSelectionsComplete, invalid: ui.hobcutoutIsInvalid, accessible: ui.sinkSelectionsComplete }">
                            {{ ui.t('hobcutoutTabTitle') }}
                        </router-link>

                        <router-link :to="`/${state.productFamilyCode}/functional-area`" v-if="ui.functionalAreaAvailable" :class="{ accessible: ui.hobcutoutSelectionsComplete }" >
                            {{ ui.t('functionalAreaTabTitle') }}
                        </router-link>

                        <router-link :to="`/${state.productFamilyCode}/accessories`" v-if="!state.disableAccessories" :class="{ complete: state.accessories.length > 0, accessible: ui.hobcutoutSelectionsComplete }">
                            {{ ui.t('accessoriesTabTitle') }}
                        </router-link>

                        <router-link to="/summary" class="no-state" :class="{ accessible: ui.hobcutoutSelectionsComplete }">
                            {{ ui.t('summaryTabTitle') }}
                        </router-link>
                    </div>

                    <div class="menu" v-if="state.mode == 'backpanel'">
                        <router-link to="/backpanel" :class="{ complete: ui.backpanelSelectionsComplete }">
                            <span class="step">1.</span> {{ ui.t('backpanelTabTitle') }}
                        </router-link>

                        <router-link to="/backpanel-material" :class="{ complete: ui.backpanelMaterialSelectionsComplete, accessible: ui.backpanelSelectionsComplete }">
                            <span class="step">2.</span> {{ ui.t('worktopMaterialTabTitle') }}
                        </router-link>

                        <router-link to="/summary" class="no-state" :class="{ accessible: ui.backpanelMaterialSelectionsComplete }">
                            <span class="step">3.</span> {{ ui.t('summaryTabTitle') }}
                        </router-link>
                    </div>

                    <div class="menu" v-if="state.mode == ''">
                        <router-link to="/summary" class="no-state">
                            <span class="step"></span> {{ ui.t('summaryTabTitle') }}
                        </router-link>
                    </div>
                </div>
            </div>

            <popup v-model:visible="ui.navigateToStartPopupOpen" class="navigate-to-start">
                <div class="html-text" v-html="ui.texts.navigateToStartPopupText.text"></div>

                <a href="#" class="button-link" @click.prevent="gotoStart" v-html="ui.t('ok')"></a>
                <a href="#" class="button-link disabled" @click.prevent="ui.navigateToStartPopupOpen = false" v-html="ui.t('cancel')"></a>
            </popup>

            <popup v-model:visible="ui.embedPopupOpen" class="embed-popup">
                <div class="media-embed-wrapper" v-if="ui.activeEmbedMedia">
                    <div class="active-media">
                        <HoverZoomImage 
                            v-if="!ui.activeEmbedMedia.embedCode"
                            :src="ui.activeEmbedMedia.previewImage!"
                            :zoom="2.2"
                            :max-zoom="4"
                            :hold-delay="180"
                            :enable-pinch="false"
                            :enable-wheel="false" />

                        <div v-if="ui.activeEmbedMedia.embedCode" v-html="ui.activeEmbedMedia.embedCode" class="video-frame"></div>
                    </div>

                    <div class="scroll-controls">
                        <div class="left" @click="scrollMedia(-300)">
                            <i class="icon-angle-left"></i>
                        </div>
                        <div class="right" @click="scrollMedia(300)">
                            <i class="icon-angle-right"></i>
                        </div>
                    </div>

                    <div class="other-media">
                        <div v-for="embedMedia in filterToDisplayMedia(ui.embedMedia)" :key="embedMedia.previewImage!" :class="embedClasses(embedMedia)" @click.stop="setActiveEmbedMedia(embedMedia)">
                            <img :src="embedMedia.previewImage!" />
                        </div>
                    </div>
                </div>
            </popup>

            <router-view @language-changed="setLanguage" />

        </template>
    </div>
</template>

<script async setup lang="ts">
import axios from "axios";
// import VueAnalytics from 'vue-analytics';
import { useCart, useConfiguration, useState, useUI } from "../store";
import type { Configuration, EmbedMedia } from "../models";
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, ref, getCurrentInstance, watch, nextTick } from "vue";
import { bootstrap, setOptions } from "vue-gtag";
import HoverZoomImage from "./HoverZoomImage.vue";

const loading = ref(true);
const viewportTooNarrow = ref(false);

const { state } = useState();
const { ui } = useUI();
const { cart } = useCart();
const { configuration } = useConfiguration();

const router = useRouter();
const route = useRoute();

const ready = ref(false);

const designerLanguageKey = "DesingerLang";

function setActiveEmbedMedia(embedMedia: EmbedMedia) {        
    ui.activeEmbedMedia = embedMedia;  
}

function loadAnalytics() {
    if (!configuration.analyticsCode)
        return;

    setOptions({
        config: {
            id: configuration.analyticsCode,
            params: {
                "dimension1": state.proshop,
                "dimension2": state.fusion,
                "dimension3": state.isProshopUser,
                "dimension4": state.vertex
            }
        },
    });

    bootstrap().then((gtag) => {
    });
}

const summaryStepNumber = computed(() => state.disableAccessories ? "5." : "6.");

function windowResize() {
    viewportTooNarrow.value = window.innerWidth < 980;

    ui.isTabletView = window.innerWidth < 1300;
}

function scrollMedia(scrollDelta: number) {
    let scrollContent = document.getElementsByClassName("other-media")[0];
    let scrollPosition = scrollContent.scrollLeft;
    let newScrollPosition =  scrollPosition + scrollDelta;
    
    if (newScrollPosition < 0)
        newScrollPosition = 0;
    
    scrollContent.scrollLeft = newScrollPosition;
}

function gotoStart() {
    cart.resetState();

    cart.resetCart();

    ui.navigateToStartPopupOpen = false;

    ui.exitConfirmed = true;

    loading.value = false;

    router.push("/");

    ui.exitConfirmed = false;
}

const setLanguage = async (code: string) => {
    ui.languagesVisible = false;

    (window as any)._language = code;

    await loadUi(code);
}

const toggleCart = async () => {
    if (!ui.minicartVisible) {
        await cart.populateSummary();
    }

    if (cart.products.length <= 0)
        return;

    ui.minicartVisible = !ui.minicartVisible;
}

function embedClasses(embedMedia: EmbedMedia) {
    let classes = [ "media" ];

    if (ui.activeEmbedMedia!.previewImage == embedMedia.previewImage)
        classes.push("active");

    if (embedMedia.embedCode)
        classes.push("video");

        return classes;
}

function closeLayers() {
    ui.helpVisible = false;
    ui.minicartVisible = false;
    ui.languagesVisible = false;
}

const loadUi = async (code: string | null) => {
    loading.value = true;

    let url = "configuration" + (code ? "?language=" + code : "");

    if (import.meta.env.DEV) {
        url = `http://stala.local/designer2/${url}`
    }

    let { data } = await axios.get<Configuration>(url);

    configuration.models = data.models;
    configuration.translations = data.translations;
    configuration.designerCodeRegexp = data.designerCodeRegexp;
    configuration.backpanelCodeRegexp = data.backpanelCodeRegexp;
    configuration.currencyCode = data.currencyCode;
    configuration.taxPercent = data.taxPercent;
    configuration.texts = data.texts;
    configuration.blocks = data.blocks;
    configuration.images = data.images;
    configuration.checkoutUrl = data.checkoutUrl;
    configuration.conditionalAdditionalCostProducts = data.conditionalAdditionalCostProducts;
    configuration.trapProductCode = data.trapProductCode;
    configuration.hidePrices = data.hidePrices;
    configuration.languages = data.languages;
    configuration.chatEnabled = data.chatEnabled;
    configuration.language = data.language;
    configuration.analyticsCode = data.analyticsCode;
    configuration.homePageLink = data.homePageLink;

    ui.init(state, data);

    if (cart) {
        // cart.ui = state.ui;
        // cart.config = data;
    }
    else {
        // cart.init(state, ui, data);
        // cart = new Cart(state, ui, data);
    }

    await cart.populateSummary();
    await cart.refreshCart();

    // SV designer does not allow to select worktop location.
    // Therefore we allow raised back. On other shops, raised back on island is restricted combination 
    state.disabledWorktopLocation = configuration.language == "sv";
    state.restrictRaisedBackOnIsland = configuration.language != "sv";

    loading.value = false;

    try {
        if (code)
            sessionStorage.setItem(designerLanguageKey, code);
    }
    catch {
        // Nop if no access to ss
    }

    nextTick(() => loading.value = false);//hack
}

const loadConfiguration = async (code: string | null) => {
    // store.state = new State();

    await loadUi(code);

    ready.value = true;
}

const unavailableViewStyles = () => {
    return {
        "background-image": "url('" + ui.texts.frontpageMain.images[0] + "')"
    }
}

// get unavailableViewStyles() {
//     return {
//         "background-image": "url('" + ui.texts.frontpageMain.images[0] + "')"
//     }
// }

const hashChangeHandler = () => {
    var target = window.location.hash;
    router.push(target.substring(1, target.length));
}

const init = async () => {
    if (/Trident\/|MSIE/.test(window.navigator.userAgent)) {
        window.addEventListener('hashchange', hashChangeHandler);
    }

    axios.defaults.baseURL = window.location.pathname;

    document.body.addEventListener("click", closeLayers);

    window.addEventListener('beforeunload', (e) =>  {
        if (state.dirty && !state.proshop && state.externalEmbed) {
            (e || window.event).returnValue = ui.texts.navigateToStartPopupText.text;

            return ui.texts.navigateToStartPopupText.text;
        }
    });

    window.addEventListener("resize", windowResize);

    let language = route.params.lang?.toString() ??
        new URLSearchParams(location.hash.substring(2)).get("language");

    if (!language) {
        try {
            const lsValue = sessionStorage.getItem(designerLanguageKey);
            if (lsValue)
                language = lsValue;
        }
        catch {
            // nop
        }
    }

    if (language)
    {
        const w = window as any;
        w._language = language;
    }
    else
        language = (window as any)._language;

    await loadConfiguration(language);

    let { data } = await axios.get<boolean>("IsProshopUser");

    state.isProshopUser = data;

    let code = route.query.code;

    state.allowNewDesigner = state.allowNewDesigner || route.query.mode == "designer-next-preview";

    state.proshop = route.query.proshop == "1";

    state.vertex = route.query.vertex == "1";

    state.fusion = route.query.fusion == "1";

    state.store = route.query.store == "1";

    state.prodlib = route.query.prodlib == "1";

    if (route.query.version == "true") {
        console.log("3.0");
    }

    state.disableAccessories = (state.externalEmbed && route.query.disableAccessories == "1");

    if (!state.allowNewDesigner) {
        state!.disabledBackStyle = true;
        state!.productFamilyCode = "seven";
        state!.backStyle = "BackNormal";
    }

    loadAnalytics();

    let targetRoute = "/";

    if (route.params.family && ["seven", "one"].includes(route.params.family.toString())) {
        state.productFamily = route.params.family.toString();
        targetRoute =  `/${state.productFamilyCode}/worktop`;
    }

    if (code) {
        await cart.setDesignerCode(code as string);

        if (cart.isCurrentCodeValidWorktopCode) {
            targetRoute = `/${state.productFamilyCode}/worktop`;
        }
        else if (cart.isCurrentCodeValidBackpanelCode) {
            targetRoute = "/backpanel";
        }
    }
    else if (state.externalEmbed) {
        if (router.currentRoute.value.path == "/")
            targetRoute = `/family`;
        else
            targetRoute = router.currentRoute.value.path;
    }

    router.push(targetRoute);

    windowResize();
    //await this.testData();
}

//await init();

const IMAGE_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "tiff"
]);

const VIDEO_EXTENSIONS = new Set([
  "mp4", "webm", "ogg", "mov", "avi", "mkv", "flv", "wmv", "m4v"
]);

function getFileExtension(path: string | null): string | null {
  if (!path)
      return null;

  const cleanPath = path.split(/[?#]/)[0];
  const match = cleanPath.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : null;
}

function isDisplayMedia(media: EmbedMedia): boolean {
    if (media.embedCode)
        return true;

    const ext = getFileExtension(media.previewImage);
    return ext != null && (IMAGE_EXTENSIONS.has(ext) || VIDEO_EXTENSIONS.has(ext));
}

function filterToDisplayMedia(media: EmbedMedia[] | null) {
    if (!media)
        return null;

    return media.filter(isDisplayMedia);
}

onMounted(async () => await init());

</script>
